import useAppStore from "../stores/AppStore"
import api from "./api"

// ── Tipos ──────────────────────────────────────────────────────────────────

export type RecenciaModo = "" | "INATIVOS" | "ATIVOS"

// 0 = Delivery, 1 = Retirada, 2 = Mesa
export type TipoPedido = 0 | 1 | 2

export interface FiltroSegmentacao {
    recenciaModo?: RecenciaModo
    recenciaDias?: number
    minPedidos?: number
    dataInicio?: string
    dataFim?: string
    tiposPedido?: TipoPedido[]
}

export interface InscritosResposta {
    quantidadeInscritos: number
}

export interface AlcanceResposta {
    alcance: number
    filtroVazio: boolean
    descricaoFiltros: string
}

export interface EnvioResposta {
    enviados: number
    filtroVazio: boolean
    descricaoFiltros: string
    mensagem: string
}

export interface HistoricoItem {
    id: number
    titulo: string
    mensagem: string
    descricaoFiltros: string
    quantidadeAlvo: number
    quantidadeEnviados: number
    dataEnvio: string
    dataEnvioMillis: number
}

export interface ErroApi {
    erro: string
}

export function isErroApi(valor: unknown): valor is ErroApi {
    return typeof valor === "object" && valor !== null && "erro" in valor
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const BASE = "notificacao-push"

// Resolve o id do estabelecimento em uso para a âncora da operação. O interceptor global
// (api.js) só envia o header `estabelecimento` quando o usuário trocou de loja manualmente
// (localStorage.estabelecimentoAtualCF). Numa sessão de login normal esse valor não existe,
// e sem header o backend cai em "primeira loja do usuário" — que numa rede pode não ser a
// operação correta (ex.: admin mostra 9 inscritos, webservice retorna 0). Aqui garantimos
// sempre um id: loja selecionada → loja logada.
function resolverEstabelecimentoId(): number | undefined {
    try {
        const atual = JSON.parse(localStorage.getItem("estabelecimentoAtualCF") || "null")
        if (atual?.id) return atual.id
    } catch {
        /* ignore */
    }
    return useAppStore.getState().configuracoes?.user?.parametros?.estabelecimento?.id
}

function cabecalhos(autorizacao: string) {
    const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Authorization: autorizacao,
    }
    const estabelecimentoId = resolverEstabelecimentoId()
    if (estabelecimentoId) headers.estabelecimento = String(estabelecimentoId)
    return headers
}

function tratarErro(error: unknown): ErroApi {
    const axiosError = error as { message?: string; response?: { data?: unknown } }
    const data = axiosError.response?.data
    if (isErroApi(data)) return { erro: data.erro }
    if (axiosError.message === "Network Error") return { erro: "Verifique sua conexão!" }
    return { erro: axiosError.message || "Erro desconhecido" }
}

// ── Endpoints ────────────────────────────────────────────────────────────────

// GET /notificacao-push/inscritos — total de inscritos ativos da operação.
export async function obterInscritos(
    autorizacao: string
): Promise<InscritosResposta | ErroApi> {
    try {
        const { data } = await api.get<InscritosResposta>(`${BASE}/inscritos`, {
            headers: cabecalhos(autorizacao),
        })
        return data
    } catch (error) {
        return tratarErro(error)
    }
}

// POST /notificacao-push/alcance — pré-visualiza quantos inscritos casam com o filtro.
export async function calcularAlcance(
    autorizacao: string,
    filtro: FiltroSegmentacao
): Promise<AlcanceResposta | ErroApi> {
    try {
        const { data } = await api.post<AlcanceResposta>(`${BASE}/alcance`, filtro ?? {}, {
            headers: cabecalhos(autorizacao),
        })
        return data
    } catch (error) {
        return tratarErro(error)
    }
}

// POST /notificacao-push/enviar — dispara a notificação e registra no histórico.
export async function enviarNotificacao(
    autorizacao: string,
    payload: { titulo: string; mensagem: string; filtro?: FiltroSegmentacao }
): Promise<EnvioResposta | ErroApi> {
    try {
        const { data } = await api.post<EnvioResposta>(`${BASE}/enviar`, payload, {
            headers: cabecalhos(autorizacao),
        })
        return data
    } catch (error) {
        return tratarErro(error)
    }
}

// GET /notificacao-push/historico — lista os disparos, mais recentes primeiro.
export async function obterHistorico(
    autorizacao: string,
    periodo?: { dataInicio?: string; dataFim?: string }
): Promise<HistoricoItem[] | ErroApi> {
    try {
        const params: Record<string, string> = {}
        if (periodo?.dataInicio) params.dataInicio = periodo.dataInicio
        if (periodo?.dataFim) params.dataFim = periodo.dataFim
        const { data } = await api.get<HistoricoItem[]>(`${BASE}/historico`, {
            headers: cabecalhos(autorizacao),
            params,
        })
        return data
    } catch (error) {
        return tratarErro(error)
    }
}
